import axios from "axios";
import { APIClient } from "../helpers/api_helper";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";
import {
  CreateProductPayload,
  Product,
  ProductType,
  UnitOfMeasure,
  UpdateProductPayload,
} from "../types/product";
import {
  AdjustStockPayload,
  StockMovement,
  StockMovementListParams,
} from "../types/stockMovement";
import {
  InventoryBatch,
  InventoryValuationReport,
} from "../types/inventoryBatch";
import {
  InventoryBatchRecallClient,
  InventoryBatchRecallMeta,
} from "../types/inventoryBatchRecall";

const api = new APIClient();

const RESOURCE = "/products";

export type { Product, ProductType, UnitOfMeasure, CreateProductPayload, UpdateProductPayload };
export type { StockMovement, StockMovementType, StockMovementOrigin, AdjustStockPayload, StockMovementListParams } from "../types/stockMovement";

export interface ProductListParams extends PaginationParams {
  unit_id?: number;
  product_type?: ProductType;
  category_id?: number;
  search?: string;
  brand?: string;
  supplier_name?: string;
  expiring_within_days?: number;
  has_opened_batches?: boolean | number;
  open_expiring_within_days?: number;
}

export const getProducts = (
  params?: ProductListParams
): Promise<PaginatedApiResponse<Product>> =>
  api.get<PaginatedApiResponse<Product>>(RESOURCE, params);

export const getProduct = (id: number): Promise<ApiResponse<Product>> =>
  api.get<ApiResponse<Product>>(`${RESOURCE}/${id}`);

export const createProduct = (
  payload: CreateProductPayload
): Promise<ApiResponse<Product>> =>
  api.create<ApiResponse<Product>>(RESOURCE, payload);

export const updateProduct = (
  id: number,
  payload: UpdateProductPayload
): Promise<ApiResponse<Product>> =>
  api.update<ApiResponse<Product>>(`${RESOURCE}/${id}`, payload);

export const deleteProduct = (
  id: number
): Promise<ApiResponse<Record<string, never>>> =>
  api.delete<ApiResponse<Record<string, never>>>(`${RESOURCE}/${id}`);

export const uploadProductPhoto = (
  id: number,
  photo: File
): Promise<ApiResponse<Product>> => {
  const formData = new FormData();
  formData.append("photo", photo);

  return axios.post(`${RESOURCE}/${id}/photo`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }) as Promise<ApiResponse<Product>>;
};

export const deleteProductPhoto = (
  id: number
): Promise<ApiResponse<Product>> =>
  api.delete<ApiResponse<Product>>(`${RESOURCE}/${id}/photo`);

export const getProductStockMovements = (
  productId: number,
  params?: StockMovementListParams
): Promise<PaginatedApiResponse<StockMovement>> =>
  api.get<PaginatedApiResponse<StockMovement>>(
    `${RESOURCE}/${productId}/stock-movements`,
    params
  );

export const adjustProductStock = (
  productId: number,
  payload: AdjustStockPayload
): Promise<ApiResponse<StockMovement>> =>
  api.create<ApiResponse<StockMovement>>(
    `${RESOURCE}/${productId}/stock-movements/adjust`,
    payload
  );

export const getProductBatches = (
  productId: number,
  params?: { include_zero?: boolean; opened?: boolean }
): Promise<ApiResponse<InventoryBatch[]>> =>
  api.get<ApiResponse<InventoryBatch[]>>(`${RESOURCE}/${productId}/batches`, params);

export const updateProductBatch = (
  productId: number,
  batchId: number,
  payload: { expires_at?: string | null; cost_price_cents?: number }
): Promise<ApiResponse<InventoryBatch>> =>
  api.update<ApiResponse<InventoryBatch>>(
    `${RESOURCE}/${productId}/batches/${batchId}`,
    payload
  );

export const markProductBatchOpened = (
  productId: number,
  batchId: number,
  payload?: { open_shelf_life_days?: number; opened_at?: string }
): Promise<ApiResponse<InventoryBatch>> =>
  api.create<ApiResponse<InventoryBatch>>(
    `${RESOURCE}/${productId}/batches/${batchId}/mark-opened`,
    payload ?? {}
  );

export const getProductBatchRecall = (
  productId: number,
  batchId: number,
  params?: {
    page?: number;
    per_page?: number;
    q?: string;
    has_phone?: boolean | number;
    audit?: boolean | number;
  }
): Promise<PaginatedApiResponse<InventoryBatchRecallClient> & { meta: InventoryBatchRecallMeta }> =>
  api.get(
    `${RESOURCE}/${productId}/batches/${batchId}/recall`,
    params
  );

export const notifyProductBatchRecall = (
  productId: number,
  batchId: number
): Promise<ApiResponse<{ notified_count: number }>> =>
  api.create<ApiResponse<{ notified_count: number }>>(
    `${RESOURCE}/${productId}/batches/${batchId}/recall/notify`,
    {}
  );

export const downloadProductBatchRecallCsv = async (
  productId: number,
  batchId: number,
  params?: { q?: string; has_phone?: boolean | number }
): Promise<Blob> => {
  const search = new URLSearchParams();
  if (params?.q) {
    search.set("q", params.q);
  }
  if (params?.has_phone) {
    search.set("has_phone", "1");
  }
  const qs = search.toString();
  const path = `${RESOURCE}/${productId}/batches/${batchId}/recall/export${
    qs ? `?${qs}` : ""
  }`;

  const blob = await axios.get<Blob>(path, { responseType: "blob" });

  return blob as unknown as Blob;
};

export const triggerBatchRecallCsvDownload = (
  blob: Blob,
  filename: string
): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const getInventoryValuationReport = (
  unitId: number
): Promise<ApiResponse<InventoryValuationReport>> =>
  api.get<ApiResponse<InventoryValuationReport>>("/inventory/valuation-report", {
    unit_id: unitId,
  });

export const getExpiringInventory = (
  params?: { unit_id?: number; within_days?: number }
): Promise<ApiResponse<InventoryBatch[]>> =>
  api.get<ApiResponse<InventoryBatch[]>>("/inventory/expiring", params);

export const getOpenExpiringInventory = (
  params?: { unit_id?: number; within_days?: number }
): Promise<ApiResponse<InventoryBatch[]>> =>
  api.get<ApiResponse<InventoryBatch[]>>("/inventory/open-expiring", params);
